import { uiText } from '../i18n/uiText';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, CalendarDays, ClipboardList, RefreshCw } from 'lucide-react';
import PageIntro from '../components/ui/PageIntro';
import Badge from '../components/ui/Badge';
import StatusBadge from '../components/ui/StatusBadge';
import { useDashboard } from '../hooks/useData';
import { formatDate, formatRelativeDueDate, getFullName } from '../utils/formatters';
import { useAuth } from '../store/AuthContext';
import { isAuditeeRole } from '../utils/permissions';
import { StatStrip } from '../components/common/WorkspaceSummary';
import AuditeeDashboard from '../components/auditee/AuditeeDashboard';
import { safeInternalRoute } from '../utils/safeNavigation';
import DashboardLaunchpad from '../components/ui/DashboardLaunchpad';

function SectionSkeleton({ rows = 4 }) {
  return (
    <div className="workspace-list">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="workspace-skeleton-row">
          <div className="workspace-skeleton-line workspace-skeleton-line-lg" />
          <div className="workspace-skeleton-line" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ title, text, actionTo, actionLabel }) {
  return (
    <div className="workspace-empty-state">
      <div className="workspace-empty-state-title">{title}</div>
      <div className="workspace-empty-state-text">{text}</div>
      {actionTo && actionLabel ? <Link className="btn btn-outline btn-sm" to={actionTo}>{actionLabel}</Link> : null}
    </div>
  );
}

function SimpleList({ items, loading, emptyState, renderItem }) {
  if (loading) return <SectionSkeleton />;
  if (!items?.length) return emptyState;
  return <div className="workspace-list">{items.map(renderItem)}</div>;
}

function buildAuditeeChecklist(rawChecklist, data) {
  const items = Array.isArray(rawChecklist) ? rawChecklist : [];
  const findItem = (...keys) => items.find((item) => keys.includes(item.id) || keys.includes(item.key));
  const selfCheck = findItem('self_check', 'self-check');
  const auditStep = findItem('audit', 'audits');
  const hasAudits = Boolean(
    auditStep?.done
    || (data?.stats?.active_audits_count || 0) > 0
    || (data?.stats?.week_calendar_count || 0) > 0
    || (data?.active_audits || []).length
    || (data?.week_calendar || []).length
  );

  return [
    {
      id: 'self_check',
      key: 'self_check',
      title: uiText("Заполните self-check"),
      done: Boolean(selfCheck?.done),
      to: '/self-check',
    },
    {
      id: 'audit',
      key: 'audit',
      title: uiText("Проверьте запланированные аудиты"),
      done: hasAudits,
      to: '/audits',
    },
  ];
}

export default function DashboardPage() {
  const { user } = useAuth();
  const dashboardState = useDashboard();
  const [hiddenChecklist, setHiddenChecklist] = useState(() => { try { return localStorage.getItem('workspace_onboarding_hidden') === '1'; } catch { return false; } });
  const [focusedSection, setFocusedSection] = useState('');
  const [expandedLists, setExpandedLists] = useState({});
  const isCustomer = isAuditeeRole(user);
  const data = dashboardState.data || {};
  const stats = data.stats || {};
  const attentionAudits = data.attention?.audits || [];
  const attentionIssues = data.attention?.issues || [];
  const attention = [...attentionAudits.map((item) => ({ type: 'audit', ...item })), ...attentionIssues.map((item) => ({ type: 'issue', ...item }))];
  const activeAudits = data.active_audits || [];
  const weekCalendar = data.week_calendar || [];
  const recentActivity = data.recent_activity || [];
  const checklist = isCustomer ? buildAuditeeChecklist(data.onboarding, data) : (data.onboarding || []);
  const notifications = data.notifications || [];
  const procedureActions = isCustomer ? [] : (data.procedure_actions || []);
  const myCompany = data.my_company || null;
  const hasChecklist = checklist.some((item) => !item.done);
  const checklistCompleted = useMemo(() => checklist.filter((item) => item.done).length, [checklist]);

  if (isCustomer) {
    return (
      <AuditeeDashboard
        user={user}
        data={data}
        loading={dashboardState.loading}
        error={dashboardState.error}
        onReload={dashboardState.reload}
      />
    );
  }

  const revealSection = (sectionId) => {
    setFocusedSection(sectionId);
    window.requestAnimationFrame(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <div className="workspace-page ap-studio-dashboard">
      <PageIntro
        title={uiText("Обзор")}
        description={uiText("Здравствуйте, {v0}. Здесь собраны ваши актуальные задачи и сроки.", {v0: (user?.full_name || uiText("пользователь"))})}
        aside={<div className="workspace-side-actions"><button type="button" className="btn btn-outline" onClick={() => dashboardState.reload()} aria-label={uiText("Обновить данные")}><RefreshCw size={15} />{uiText(" Обновить")}</button></div>}
      />

      {dashboardState.error ? <div className="alert alert-error">{dashboardState.error}</div> : null}
      <DashboardLaunchpad userId={user?.id} audits={activeAudits} loading={dashboardState.loading} error={dashboardState.error} />

      {myCompany ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <span className="workspace-link-panel-icon"><Building2 size={18} /></span>
              <div style={{ minWidth: 0 }}>
                <div className="workspace-section-label">{uiText("Моя организация")}</div>
                <div className="workspace-item-title" style={{ marginTop: 2 }}>{myCompany.name || myCompany.legal_name || uiText("Компания #{v0}", {v0: (myCompany.id)})}</div>
                <div className="workspace-muted">{[myCompany.legal_name, myCompany.site_address || myCompany.address, myCompany.email].filter(Boolean).join(' · ') || uiText("Данные организации не заполнены")}</div>
              </div>
            </div>
            <Link className="btn btn-outline btn-sm" to={isCustomer ? '/self-check' : '/companies'}>{isCustomer ? uiText("Открыть анкету") : uiText("Открыть организации")}</Link>
          </div>
        </div>
      ) : null}

      <StatStrip items={[
        {label:uiText("Требуют действий"),value:stats.attention_count || 0,tone:'danger',controls:'dashboard-attention',active:focusedSection==='dashboard-attention',onClick:()=>revealSection('dashboard-attention')},
        {label:uiText("Активные аудиты"),value:stats.active_audits_count || 0,controls:'dashboard-active-audits',active:focusedSection==='dashboard-active-audits',onClick:()=>revealSection('dashboard-active-audits')},
        {label:uiText("На этой неделе"),value:stats.week_calendar_count || 0,controls:'dashboard-week-calendar',active:focusedSection==='dashboard-week-calendar',onClick:()=>revealSection('dashboard-week-calendar')},
        {label:uiText("Шаги по процессу"),value:stats.procedure_actions_count || 0,tone:'warning',controls:'dashboard-procedure-actions',active:focusedSection==='dashboard-procedure-actions',onClick:()=>revealSection('dashboard-procedure-actions')},
      ]}/>

      <div className="workspace-grid-two">
        {!isCustomer ? (
          <div id="dashboard-procedure-actions" className={`card dashboard-reveal-section ${focusedSection === 'dashboard-procedure-actions' ? 'is-focused' : ''}`}>
            <div className="card-header"><div className="card-title">{uiText("Следующие шаги по регламенту")}</div><Link className="btn btn-ghost btn-sm" to="/audits?view=my_active">{uiText("Открыть всё")}</Link></div>
            <div className="card-body">
              <SimpleList
                items={expandedLists.procedure ? procedureActions : procedureActions.slice(0, 3)}
                loading={dashboardState.loading}
                emptyState={<EmptyState title={uiText("Сейчас нет обязательных шагов")} text={uiText("По активным аудитам не найдено критичных пропусков процесса. Можно продолжать текущую работу по чек-листам и замечаниям.")} actionTo="/audits?view=my_active" actionLabel={uiText("Открыть активные аудиты")} />}
                renderItem={(item) => (
                  <Link key={`${item.audit_id}-${item.key}`} to={safeInternalRoute(item.to, `/audits/${item.audit_id}`)} className="workspace-list-item workspace-procedure-item">
                    <div className="workspace-procedure-copy">
                      <div className="workspace-item-title">{item.title}</div>
                      <div className="workspace-muted">{item.audit_title || uiText("Аудит")} · {item.current_stage_label || uiText("Процесс")} · {item.text}</div>
                    </div>
                    <div className="workspace-chip-row workspace-procedure-meta">
                      {item.due_date ? <div className="workspace-date-pill"><CalendarDays size={14} /> {formatDate(item.due_date)}</div> : null}
                      <Badge variant={item.tone || 'info'}>{item.tone === 'danger' ? uiText("Критично") : item.tone === 'warning' ? uiText("Важно") : item.tone === 'success' ? uiText("Готово") : uiText("Далее")}</Badge>
                    </div>
                  </Link>
                )}
              />
              {procedureActions.length > 3 && <button type="button" className="ap-button ap-button--secondary ap-studio-show-more" aria-expanded={!!expandedLists.procedure} onClick={() => setExpandedLists(v => ({ ...v, procedure: !v.procedure }))}>{expandedLists.procedure ? uiText("Свернуть список шагов") : uiText("Показать все шаги ({v0})", {v0: (procedureActions.length)})}</button>}
            </div>
          </div>
        ) : null}

        <div id="dashboard-attention" className={`card dashboard-reveal-section ${focusedSection === 'dashboard-attention' ? 'is-focused' : ''}`}>
          <div className="card-header"><div className="card-title">{uiText("Требуют моего внимания")}</div><Link className="btn btn-ghost btn-sm" to="/issues">{uiText("Открыть всё")}</Link></div>
          <div className="card-body">
            <SimpleList
              items={expandedLists.attention ? attention : attention.slice(0, 3)}
              loading={dashboardState.loading}
              emptyState={<EmptyState title={uiText("Сейчас критичных блокеров нет")} text={uiText("Это хороший знак. Откройте активные аудиты или календарь, чтобы продолжить работу по плану.")} actionTo="/audits?view=my_active" actionLabel={uiText("Мои активные аудиты")} />}
              renderItem={(item) => (
                <Link key={`${item.type}-${item.id}`} to={item.type === 'audit' ? `/audits/${item.id}` : '/issues'} className="workspace-list-item">
                  <div>
                    <div className="workspace-item-title">{item.title || `${item.type === 'audit' ? uiText("Аудит") : uiText("Замечание")} #${item.id}`}</div>
                    <div className="workspace-muted">{item.type === 'audit' ? (item.signals || []).join(' · ') : `${getFullName(item.audit?.customer, uiText("Без площадки"))} · ${formatRelativeDueDate(item.due_date)}`}</div>
                  </div>
                  <StatusBadge
                    status={item.status}
                    type={item.type === 'audit' ? 'audit' : 'issue'}
                    variant={item.type === 'audit' ? 'danger' : (item.is_overdue ? 'danger' : undefined)}
                  />
                </Link>
              )}
            />
            {attention.length > 3 && <button type="button" className="ap-button ap-button--secondary ap-studio-show-more" aria-expanded={!!expandedLists.attention} onClick={() => setExpandedLists(v => ({ ...v, attention: !v.attention }))}>{expandedLists.attention ? uiText("Свернуть список") : uiText("Показать всё ({v0})", {v0: (attention.length)})}</button>}
          </div>
        </div>
      </div>

      {(data.deadlines || []).length > 0 && <section className="card" aria-label={uiText("Ближайшие сроки")}><div className="card-header"><div className="card-title">{uiText("Ближайшие сроки")}</div></div><div className="card-body workspace-list">{data.deadlines.map((item,index)=><Link className="workspace-list-item" key={item.id || index} to={safeInternalRoute(item.to,'/audit-calendar')}><div><div className="workspace-item-title">{item.title || uiText("Срок по аудиту")}</div><div className="workspace-muted">{item.text || item.audit_title}</div></div><div className="workspace-date-pill">{formatRelativeDueDate(item.date)}</div></Link>)}</div></section>}

      {!hiddenChecklist && hasChecklist ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><div className="card-title">{uiText("Быстрый старт")}</div><Badge variant={checklistCompleted === checklist.length ? 'success' : 'warning'}>{checklistCompleted}/{checklist.length}</Badge></div>
          <div className="card-body" style={{ display: 'grid', gap: 12 }}>
            {checklist.map((item) => (
              <Link key={item.key || item.id} to={safeInternalRoute(item.to, '/dashboard')} className="workspace-check-item">
                <div className={`workspace-check-dot ${item.done ? 'is-done' : ''}`} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{item.title}</div>
                  <div className="workspace-muted">{item.done ? uiText("Шаг уже выполнен.") : uiText("Это поможет запустить работу без пустых разделов.")}</div>
                </div>
                <Badge variant={item.done ? 'success' : 'neutral'}>{item.done ? uiText("Готово") : uiText("Открыть")}</Badge>
              </Link>
            ))}
            <div><button type="button" className="btn btn-ghost btn-sm" onClick={() => { try { localStorage.setItem('workspace_onboarding_hidden', '1'); } catch {} setHiddenChecklist(true); }}>{uiText("Скрыть блок")}</button></div>
          </div>
        </div>
      ) : null}

      <div className="workspace-grid-two">
        <div id="dashboard-active-audits" className={`card dashboard-reveal-section ${focusedSection === 'dashboard-active-audits' ? 'is-focused' : ''}`}>
          <div className="card-header"><div className="card-title">{uiText("Активные аудиты")}</div><Link className="btn btn-ghost btn-sm" to="/audits?view=my_active">{uiText("Мои активные")}</Link></div>
          <div className="card-body">
            <SimpleList
              items={activeAudits}
              loading={dashboardState.loading}
              emptyState={<EmptyState title={uiText("Пока нет активных аудитов")} text={isCustomer ? uiText("Когда аудит перейдёт в работу, он появится здесь.") : uiText("Когда появится проверка в работе, она будет здесь. До этого можно открыть календарь или создать первый аудит.")} actionTo="/audits" actionLabel={uiText("Открыть аудиты")} />}
              renderItem={(item) => (
                <Link key={item.id} to={`/audits/${item.id}`} className="workspace-list-item">
                  <div>
                    <div className="workspace-item-title">{item.title || uiText("Аудит #{v0}", {v0: (item.id)})}</div>
                    <div className="workspace-muted">{item.site_name || getFullName(item.customer, uiText("Без площадки"))} · {formatDate(item.scheduled_date)}</div>
                  </div>
                  <StatusBadge status={item.status} type="audit" />
                </Link>
              )}
            />
          </div>
        </div>

        <div id="dashboard-week-calendar" className={`card dashboard-reveal-section ${focusedSection === 'dashboard-week-calendar' ? 'is-focused' : ''}`}>
          <div className="card-header"><div className="card-title">{uiText("Календарь на неделю")}</div><Link className="btn btn-ghost btn-sm" to="/audit-calendar">{uiText("Открыть календарь")}</Link></div>
          <div className="card-body">
            <SimpleList
              items={weekCalendar}
              loading={dashboardState.loading}
              emptyState={<EmptyState title={uiText("На неделю ничего не запланировано")} text={isCustomer ? uiText("Назначенные проверки появятся здесь после планирования аудитором.") : uiText("Свободное окно можно использовать, чтобы назначить новый аудит или подготовить шаблоны.")} actionTo={isCustomer ? '/audits' : '/audit-calendar'} actionLabel={isCustomer ? uiText("Открыть аудиты") : uiText("Открыть календарь")} />}
              renderItem={(item) => (
                <Link key={item.id} to={`/audits/${item.id}`} className="workspace-list-item">
                  <div>
                    <div className="workspace-item-title">{item.title || uiText("Аудит #{v0}", {v0: (item.id)})}</div>
                    <div className="workspace-muted">{item.site_name || uiText("Без площадки")} · {formatDate(item.scheduled_date, 'weekday')}</div>
                  </div>
                  <div className="workspace-date-pill"><CalendarDays size={14} /> {formatRelativeDueDate(item.scheduled_date)}</div>
                </Link>
              )}
            />
          </div>
        </div>
      </div>

      <div className="workspace-grid-two">
        <div id="dashboard-recent-activity" className={`card dashboard-reveal-section ${focusedSection === 'dashboard-recent-activity' ? 'is-focused' : ''}`}>
          <div className="card-header"><div className="card-title">{uiText("Недавняя активность")}</div><Badge variant="neutral">{recentActivity.length}</Badge></div>
          <div className="card-body">
            <SimpleList
              items={recentActivity}
              loading={dashboardState.loading}
              emptyState={<EmptyState title={uiText("Пока нет недавних изменений")} text={uiText("Когда в аудитах появятся новые действия, они автоматически соберутся здесь.")} actionTo="/audits" actionLabel={uiText("Открыть аудиты")} />}
              renderItem={(item) => (
                <Link key={item.id} to={safeInternalRoute(item.to, '/audits')} className="workspace-list-item">
                  <div>
                    <div className="workspace-item-title">{item.title}</div>
                    <div className="workspace-muted">{item.meta}</div>
                  </div>
                  <div className="workspace-date-pill"><ClipboardList size={14} /> {formatDate(item.date, 'datetime')}</div>
                </Link>
              )}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-header"><div className="card-title">{uiText("Новые уведомления")}</div><Link className="btn btn-ghost btn-sm" to="/notifications">{uiText("Открыть всё")}</Link></div>
          <div className="card-body">
            <SimpleList
              items={notifications}
              loading={dashboardState.loading}
              emptyState={<EmptyState title={uiText("Новых уведомлений нет")} text={uiText("Назначения, комментарии и изменения статуса появятся здесь, как только произойдут.")} actionTo="/notifications" actionLabel={uiText("Открыть центр уведомлений")} />}
              renderItem={(item) => (
                <Link key={item.id} to={safeInternalRoute(item.href, '/notifications')} className="workspace-list-item">
                  <div>
                    <div className="workspace-item-title">{item.title}</div>
                    <div className="workspace-muted">{item.text}</div>
                  </div>
                  <Badge variant={item.unread ? 'warning' : 'neutral'}>{item.unread ? uiText("Новое") : uiText("Просмотрено")}</Badge>
                </Link>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
